import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Notify,
  Tooltip, useConnectWalletModal,
} from '@airdao/ui-library';
import Select from '../../components/Select';
import Modal from '../../components/Modal';
import useBalance from '../../hooks/getBalance';
import useMarkets from '../../contexts/MarketsContext/useMarkets';
import {
  MarketContextType,
  Token,
} from '../../contexts/MarketsContext/provider.interface';
import { getTokenByAddress } from '../../utils/getTokenByAddress';
import ConfirmModal from './components/ConfirmModal';
import {
  createMarketTabs,
  DISCOUNT,
  FIXED_EXPIRY,
  FIXED_TERM,
  FPA,
  fpaFields,
  FPDA,
  INITIAL_DISCOUNT,
  INITIAL_PRICE,
  marketTypes,
  marketTypesDescr,
  MAX_DISCOUNT,
  MINIMUM_PRICE,
  OFDA,
  ofdaFields,
  ORACLE_ADDRESS,
  OSDA,
  osdaFields,
  PRICE_DISCOUNT,
  SDA,
  sdaFields,
  simpleFormFields,
  TARGET_INTERVAL_DISCOUNT,
  vestingOptions,
  vestingTypes,
  vestingTypesDescr,
} from '../../utils/constants';
import useBondContracts from '../../contexts/BondContractContext/useBondContracts';
// @ts-ignore
import { ContractNames } from '@airdao/airdao-bond';
import { ContractNames as PeripheryNames } from '@airdao/bond-periphery';
import { parseUnits } from 'ethers/lib/utils';
import DatePicker from '../../components/DatePicker';
import Range from '../../components/Range';
import { utils } from 'ethers';
import { createMarket } from './create.services';
import { Link } from 'react-router-dom';
import { isTimeDifferenceGreaterThan24Hours } from '../../utils/isTimeDifferenceGreaterThan24Hours';
import MarketData from './components/MarketData';
import { useNavigate } from 'react-router-dom';
import { hoursBetween } from '../../utils/hoursBetween';
import { formatDecimals } from '../../utils/formatDecimals';
import info from '../../assets/icons/info.svg';
import Tab from '../../components/Tab';
import arrow from '../../assets/icons/arrow-left.svg';

import useBondPeriphery from '../../contexts/BondPeripheryContext/useBondPeriphery';
import { useAccount } from 'wagmi';

const CreateBond = () => {
  const navigate = useNavigate();
  const contracts = useBondContracts();
  const peripheryContracts = useBondPeriphery();
  const { tokens }: MarketContextType = useMarkets();
  const { address: account, chainId } = useAccount();
  const { toggleModal } = useConnectWalletModal();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [vesting, setVesting] = useState('');
  const [vestingExpired, setVestingExpired] = useState('');
  const [token, setToken] = useState('');
  const [quoteToken, setQuoteToken] = useState('');
  const [amount, setAmount] = useState<string>('');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState<any>(null);
  const [depositInterval, setDepositInterval] = useState(1);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [type, setType] = useState(FPA);
  const [additionalInfo, setAdditionalInfo] = useState<{
    [key: string]: any;
  }>({});

  const [vestingType, setVestingType] = useState(FIXED_TERM);
  const [quotePrice, setQuotePrice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(createMarketTabs[0].value);

  const { balance, bnBalance } = useBalance(token);

  useEffect(() => {
    let additionalFields;

    if (activeTab === 'simple') {
      additionalFields = simpleFormFields;
    } else {
      switch (type) {
        case OFDA:
          additionalFields = ofdaFields;
          break;
        case SDA:
          additionalFields = sdaFields;
          break;
        case OSDA:
          additionalFields = osdaFields;
          break;
        default:
          additionalFields = fpaFields;
      }
    }
    setAdditionalInfo(additionalFields);
  }, [type, activeTab]);

  useEffect(() => {
    setErrors({});
  }, [additionalInfo, vestingExpired]);

  useEffect(() => {
    if (tokens.length) {
      setToken(tokens.find(el => el.contractAddress === '0x0000000000000000000000000000000000000000')?.contractAddress || '');
    }
  }, [tokens]);

  useEffect(() => {
    setErrors(state => ({
      ...state,
      quoteToken: '',
    }));
  }, [quoteToken]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const decimals = e.target.value.split('.')[1];

    if (
      /^\d*\.?\d*$/.test(e.target.value) &&
      e.target.value !== '.' &&
      e.target.value !== '00' &&
      (!decimals ||
        decimals.length <=
          (getTokenByAddress(token, tokens)?.displayPrecision || 0))
    ) {
      handleErrors('amount', '');
      setAmount(e.target.value);
    }
  };

  const handlePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (/^\d*\.?\d*$/.test(e.target.value)) {
      setQuotePrice(e.target.value);
    }
  };

  const setMaxValue = () => {
    setAmount(formatDecimals(balance, token, tokens).toString());
  };

  const handleErrors = (key: string, value: string) => {
    setErrors(state => ({
      ...state,
      [key]: value,
    }));
  };

  const handleOnReviewPress = () => {
    if (
      isFieldActive(ORACLE_ADDRESS) &&
      !utils.isAddress(additionalInfo[ORACLE_ADDRESS])
    ) {
      handleErrors('oracle', 'Invalid address');
    } else if (
      vestingType === FIXED_EXPIRY &&
      vestingExpired &&
      new Date(vestingExpired) < new Date(endDate)
    ) {
      handleErrors('expired', 'Expire date should be after market end date');
    } else if (utils.parseUnits(amount).gt(bnBalance)) {
      handleErrors('amount', 'Insufficient balance');
    } else if (token === quoteToken) {
      handleErrors('quoteToken', 'Select token different from payout token');
    } else {
      setIsOpen(!isOpen);
    }
  };

  const normalizeTokens = (tokensList: Token[]) => {
    return tokensList.filter(el => el.contractAddress !== '0xd8dd0273D31c1cd9Dba104DaCA7C1dfEE4f7b805' && el.contractAddress !== '0xC15891E4dE2793726c20F53EcA6FB6319968E5F3').map(el => ({
      value: el.contractAddress,
      label: (
        <span className="token-option">
          <img src={el.logoUrl} alt="logo" className="token-option__logo" />
          {el.symbol.toUpperCase()}
        </span>
      ),
    }));
  };

  const payoutTokenData = useMemo(() => {
    return getTokenByAddress(token, tokens);
  }, [token]);

  const quoteTokenData = useMemo(() => {
    return getTokenByAddress(quoteToken, tokens);
  }, [quoteToken]);

  const handleSubmit = async () => {
    const duration = endDate.getTime() - startDate.getTime();

    const contractName = vestingType + type;
    const settings: any = {
      contractName: ContractNames[contractName],
      payoutToken: token,
      payoutDecimals: payoutTokenData?.decimals,
      payoutPrice: payoutTokenData?.price,
      quoteToken,
      quoteDecimals: quoteTokenData?.decimals,
      capacityInQuote: false,
      capacity: parseUnits(amount, payoutTokenData?.decimals),
      depositInterval: depositInterval * 60 * 60,
      start:
        startDate.getTime() <= new Date().getTime()
          ? 0
          : Math.trunc(startDate.getTime() / 1000),
      duration: Math.trunc(duration / 1000),
      vesting:
        vestingType === FIXED_TERM
          ? vesting
          : new Date(vestingExpired).getTime() / 1000,
      ...additionalInfo,
      ...(type === FPDA ? { discountTierManager: peripheryContracts?.getContractByName(PeripheryNames.EngagementDiscountTierManager).address } : {}),
    };

    if (type === FPA || type === FPDA) {
      settings.quotePrice = +quotePrice || quoteTokenData?.price;
    }
    if (type === SDA) {
      settings.quotePrice = quoteTokenData?.price;
    }
    if (additionalInfo[DISCOUNT]) {
      settings.discount = +additionalInfo[DISCOUNT];
    }
    setIsLoading(true);
    Notify.loading('Transaction in progress.', null, {
      toastId: 'create-loading',
    });
    try {
      await createMarket(contracts, type, settings).then(() => {
        Notify.success('Success', null, { autoClose: 5000 });
        navigate('/');
      });
    } catch (e: any) {
      console.log(e);
      if (e.code !== 'ACTION_REJECTED') {
        Notify.error('Error', null, {});
      }
    }
    setIsOpen(false);
    setIsLoading(false);
    Notify.dismiss('create-loading');
  };

  const minEndDate = useMemo(() => {
    return new Date(startDate.getTime() + 86400000);
  }, [startDate]);

  useEffect(() => {
    setEndDate(minEndDate);
  }, [minEndDate]);

  useEffect(() => {
    handleErrors('date', '');
  }, [endDate]);

  const isFieldActive = (fieldName: string): boolean => {
    return additionalInfo.hasOwnProperty(fieldName);
  };

  const disabled =
    !+amount ||
    !quoteToken ||
    (vestingType === FIXED_TERM ? !vesting : !vestingExpired) ||
    Object.keys(additionalInfo).some(
      el => !additionalInfo[el] && el !== DISCOUNT,
    );

  const handleAdditionalData = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    let value: string | number = e.target.value;
    if (key === DISCOUNT) {
      value = +value;
    }
    setAdditionalInfo(state => ({
      ...state,
      [key]: value,
    }));
  };

  const handleAdditionalNumberData = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    if (/^\d*\.?\d*$/.test(e.target.value) || !e.target.value) {
      handleAdditionalData(e, key);
    }
  };

  const handleDiscount = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    if (
      (/^\d*\.?\d*$/.test(e.target.value) && +e.target.value < 100) ||
      !e.target.value
    ) {
      handleAdditionalData(e, key);
    }
  };

  const maxDepositInterval = useMemo(() => {
    if (startDate && endDate) {
      const max = hoursBetween(startDate.getTime(), endDate.getTime());
      return max > 72 ? 72 : max;
    } else {
      return 0;
    }
  }, [startDate, endDate]);

  return (
    <>
      <Link to="/" className="exchange-back" style={{ marginBottom: 30 }}>
        <img src={arrow} alt="arrow left" />
        Back
      </Link>
      <div
        className={`create-bond-wrapper ${
          activeTab === 'simple' ? 'container-simple' : 'container-advanced'
        }`}
      >
        <Tab
          tabs={createMarketTabs}
          onChange={setActiveTab}
          selectedTab={activeTab}
        />
        <h1 className="create-bond__title">Create new bond</h1>
        <div className="bond-container">
          {activeTab !== 'simple' && (
            <div className="create-bond">
              <div>
                <p className="create-bond__bottom__title">Auction type</p>
                <Select
                  options={marketTypes}
                  value={type}
                  placeholder="Select type"
                  onChange={setType}
                />
                <p className="create-bond__descr">{marketTypesDescr[type]}</p>
              </div>
              <div>
                <p className="create-bond__bottom__title">Vesting type</p>
                <Select
                  options={vestingTypes}
                  value={vestingType}
                  placeholder="Select type"
                  onChange={setVestingType}
                />
                <p className="create-bond__descr">
                  {vestingTypesDescr[vestingType]}
                </p>
              </div>
            </div>
          )}
          <div className="hr create-bond-hr" />
          <div className="create-bond">
            <div>
              <p className="create-bond__bottom__title">
                Payout amount
                <Tooltip message="Total value of the bond upon maturity.">
                  <img src={info} alt="tooltip" />
                </Tooltip>
              </p>
              <div className="create-bond__input-container">
                <div className="create-bond__input-left-side">
                  <Select
                    placeholder="AMB"
                    options={normalizeTokens(tokens)}
                    onChange={setToken}
                    value={token}
                    isSimple
                  />
                </div>
                <div className="create-bond__input-right-side">
                  <input
                    type="text"
                    value={amount}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="create-bond__input-wrapper"
                  />
                </div>
              </div>
              <div className="form-bottom-container">
                <p className="exchange-form__max">
                  Balance: {formatDecimals(balance, token, tokens)}{' '}
                  {getTokenByAddress(token, tokens)?.symbol.toUpperCase()}{' '}
                  <button onClick={setMaxValue}>Max</button>
                </p>
                <p className="exchange-form__amount">
                  ${formatDecimals(payoutTokenData?.price || '', token, tokens)}
                </p>
                {errors.amount && (
                  <span className="error-message error-message_balance">
                    {errors.amount}
                  </span>
                )}
              </div>
            </div>
            <div className="create-bond__conditional">
              <div>
                <p className="create-bond__bottom__title">
                  Quote token
                  <Tooltip message="Cryptocurrency needed to purchase the bond.">
                    <img src={info} alt="tooltip" />
                  </Tooltip>
                </p>
                <Select
                  placeholder="Select token"
                  options={normalizeTokens(tokens)}
                  error={errors.quoteToken}
                  onChange={setQuoteToken}
                  value={quoteToken}
                  className={errors.quoteToken ? 'error-color' : ''}
                />
              </div>
              {(type === FPA || type === FPDA) && (
                <div>
                  <p className="create-bond__bottom__title">
                    Set price
                    <Tooltip message="Fixed exchange rate determining payout currency covered per quote token.">
                      <img src={info} alt="tooltip" />
                    </Tooltip>
                  </p>
                  <Input
                    placeholder="$0"
                    value={quotePrice}
                    onChange={handlePrice}
                  />
                </div>
              )}
            </div>
            <div className="create-bond__conditional">
              {isFieldActive(DISCOUNT) && (
                <div>
                  <p className="create-bond__bottom__title">
                    Price discount
                    <Tooltip message="Percentage discount applied to listing price.">
                      <img src={info} alt="tooltip" />
                    </Tooltip>
                  </p>
                  <Input
                    value={additionalInfo[DISCOUNT]}
                    placeholder="0%"
                    onChange={e => handleDiscount(e, DISCOUNT)}
                  />
                </div>
              )}
              {vestingType === FIXED_TERM && (
                <div style={{ position: 'relative' }}>
                  <p className="create-bond__bottom__title">
                    Vesting length
                    <Tooltip message="Duration for which the bond remains locked before full redemption.">
                      <img src={info} alt="tooltip" />
                    </Tooltip>
                  </p>
                  <Select
                    placeholder="Select length"
                    options={vestingOptions}
                    onChange={setVesting}
                    value={vesting}
                    className="create-bond__select-vesting"
                  />
                  {errors.expired && (
                    <span className="error-message error-message-date">
                      {errors.expired}
                    </span>
                  )}
                </div>
              )}
            </div>
            {isFieldActive(ORACLE_ADDRESS) && (
              <Input
                error={errors.oracle}
                value={additionalInfo[ORACLE_ADDRESS]}
                label="Oracle contract address"
                placeholder="Paste address"
                onChange={e => handleAdditionalData(e, ORACLE_ADDRESS)}
              />
            )}
            <div className="create-bond__bottom create-bond__bottom_date">
              <div className="create-bond__bottom__container">
                <DatePicker
                  label="Start date"
                  value={startDate}
                  onChange={setStartDate}
                  minDate={new Date()}
                />
              </div>
              <div className="create-bond__bottom__container">
                <DatePicker
                  label="End date"
                  value={endDate}
                  onChange={setEndDate}
                />
              </div>
              {errors.date && (
                <span className="error-message error-message-date">
                  {errors.date}
                </span>
              )}
            </div>
            {vestingType === FIXED_EXPIRY && (
              <div style={{ position: 'relative' }}>
                <p className="create-bond__bottom__title">
                  Vesting length
                  <Tooltip message="Duration for which the bond remains locked before full redemption.">
                    <img src={info} alt="tooltip" />
                  </Tooltip>
                </p>
                <DatePicker
                  value={vestingExpired}
                  onChange={setVestingExpired}
                  minDate={minEndDate}
                />
                {errors.expired && (
                  <span className="error-message error-message-date">
                    {errors.expired}
                  </span>
                )}
              </div>
            )}
            {(isFieldActive(PRICE_DISCOUNT) ||
              isFieldActive(INITIAL_PRICE) ||
              isFieldActive(INITIAL_DISCOUNT) ||
              isFieldActive(MAX_DISCOUNT) ||
              isFieldActive(MINIMUM_PRICE)) && (
              <div className="create-bond__bottom">
                <div className="create-bond__bottom__container">
                  {isFieldActive(PRICE_DISCOUNT) && (
                    <Input
                      value={additionalInfo[PRICE_DISCOUNT]}
                      label="Price discount"
                      placeholder="0%"
                      onChange={e => handleDiscount(e, PRICE_DISCOUNT)}
                    />
                  )}
                  {isFieldActive(INITIAL_PRICE) && (
                    <Input
                      value={additionalInfo[INITIAL_PRICE]}
                      label="Initial price"
                      placeholder="$0"
                      onChange={e =>
                        handleAdditionalNumberData(e, INITIAL_PRICE)
                      }
                    />
                  )}
                  {isFieldActive(INITIAL_DISCOUNT) && (
                    <Input
                      value={additionalInfo[INITIAL_DISCOUNT]}
                      label="Initial discount"
                      placeholder="$0"
                      onChange={e =>
                        handleAdditionalNumberData(e, INITIAL_DISCOUNT)
                      }
                    />
                  )}
                </div>
                <div className="create-bond__bottom__container">
                  {isFieldActive(MAX_DISCOUNT) && (
                    <Input
                      value={additionalInfo[MAX_DISCOUNT]}
                      label="Max discount"
                      placeholder="0%"
                      onChange={e => handleDiscount(e, MAX_DISCOUNT)}
                    />
                  )}
                  {isFieldActive(MINIMUM_PRICE) && (
                    <Input
                      value={additionalInfo[MINIMUM_PRICE]}
                      label="Minimum price"
                      placeholder="$0"
                      onChange={e =>
                        handleAdditionalNumberData(e, MINIMUM_PRICE)
                      }
                    />
                  )}
                </div>
              </div>
            )}
            {isFieldActive(TARGET_INTERVAL_DISCOUNT) && (
              <Input
                value={additionalInfo[TARGET_INTERVAL_DISCOUNT]}
                label="Target interval discount"
                placeholder="$0"
                onChange={e =>
                  handleAdditionalNumberData(e, TARGET_INTERVAL_DISCOUNT)
                }
              />
            )}
            {activeTab !== 'simple' && (
              <Range
                label={`Deposit interval: ${depositInterval}h`}
                value={depositInterval}
                onChange={setDepositInterval}
                min={1}
                max={maxDepositInterval}
              />
            )}
            <div className="hr" />
            <div className="create-bond__data">
              <MarketData
                discount={additionalInfo[DISCOUNT] || 0}
                marketType={type}
                payoutToken={payoutTokenData}
                quoteToken={quoteTokenData}
                quotePrice={+quotePrice}
                capacity={amount}
                depositInterval={depositInterval * 60 * 60}
                length={
                  (new Date(endDate).getTime() -
                    new Date(startDate).getTime()) /
                  1000
                }
              />
            </div>
            <div className="create-bond__buttons">
              <div className="create-bond__buttons__cancel">
                <Link
                  to="/"
                  className="create-bond__buttons__cancel__button create-bond__buttons__cancel__button__title"
                >
                  Cancel
                </Link>
              </div>
              {account &&
              process.env.REACT_APP_CHAIN_ID === chainId?.toString() ? (
                <Button
                  type="primary"
                  size="large"
                  disabled={disabled}
                  onClick={handleOnReviewPress}
                >
                  Review and confirm
                </Button>
              ) : (
                <Button type="secondary" size="large" onClick={toggleModal}>
                  Connect wallet
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        setIsOpen={() => setIsOpen(!isOpen)}
        header="Review new bond"
        actionTitle="Create bond"
        action={handleSubmit}
        isLoading={isLoading}
        content={
          <ConfirmModal
            tokenAddress={token}
            capacity={amount}
            vesting={+vesting}
            vestingExpired={vestingExpired}
            quoteTokenName={quoteTokenData?.symbol.toUpperCase()}
            quoteToken={quoteToken}
            price={+quotePrice || getTokenByAddress(quoteToken, tokens)?.price}
          />
        }
      />
    </>
  );
};

export default CreateBond;
